/**
 * Session keeper + draft save utilities.
 *
 * Why: during a multi-hour event day, admins on multiple devices were getting
 * silently bounced to /login mid-registration when their JWT expired,
 * losing the form contents they had just typed. This module:
 *
 *  1. Adds a global axios response interceptor. When any API call returns
 *     401 *because the token is expired*, we make a single call to
 *     /auth/refresh using the stored token (which the backend still accepts
 *     within its grace window because we hit the refresh endpoint with a
 *     stored token via the same Authorization header). If refresh succeeds
 *     we transparently retry the original request once. If refresh fails
 *     (token actually invalid / revoked) we redirect to /login.
 *
 *  2. Schedules a background sliding refresh every 15 min while the tab is
 *     open. This means an admin who's actively using the app never sees an
 *     expired token, even if the original short token is < 24h.
 *
 *  3. Exposes `saveDraft(key, value)` / `loadDraft(key)` / `clearDraft(key)`
 *     to persist in-progress form state to localStorage so a token expiry
 *     redirect (or accidental tab close) doesn't lose what the admin typed.
 */

import axios from 'axios';
import { API } from '../App';

const STORAGE_TOKEN_KEY = 'token';
const DRAFT_PREFIX = 'draft:';
const REFRESH_INTERVAL_MS = 15 * 60 * 1000; // 15 min

let refreshInFlight = null;
let interceptorInstalled = false;
let refreshTimer = null;

async function performRefresh() {
  const token = localStorage.getItem(STORAGE_TOKEN_KEY);
  if (!token) throw new Error('no token');
  const res = await axios.post(
    `${API}/auth/refresh`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const newToken = res?.data?.access_token;
  if (!newToken) throw new Error('no token in refresh response');
  localStorage.setItem(STORAGE_TOKEN_KEY, newToken);
  return newToken;
}

function isAuthExpired401(err) {
  if (!err?.response) return false;
  if (err.response.status !== 401) return false;
  // Don't try to refresh the refresh call itself or the login call.
  const url = err.config?.url || '';
  if (url.includes('/auth/refresh') || url.includes('/auth/login')) return false;
  return true;
}

export function installSessionKeeper() {
  if (interceptorInstalled) return;
  interceptorInstalled = true;

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config;
      if (!original || original._retried) return Promise.reject(error);
      if (!isAuthExpired401(error)) return Promise.reject(error);

      try {
        // Coalesce concurrent refreshes so we don't stampede the backend
        // when 6 background polls hit a 401 at the same time.
        refreshInFlight = refreshInFlight || performRefresh();
        const newToken = await refreshInFlight;
        refreshInFlight = null;

        original._retried = true;
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return axios(original);
      } catch (refreshErr) {
        refreshInFlight = null;
        // Truly expired / invalid — clear and bounce to login.
        localStorage.removeItem(STORAGE_TOKEN_KEY);
        // Avoid bouncing public pages.
        if (typeof window !== 'undefined') {
          const path = window.location.pathname || '';
          if (path.startsWith('/admin') || path.startsWith('/judge')) {
            window.location.assign('/login');
          }
        }
        return Promise.reject(refreshErr);
      }
    }
  );

  // Background sliding refresh: keeps active users signed in indefinitely.
  if (refreshTimer) clearInterval(refreshTimer);
  refreshTimer = setInterval(() => {
    const token = localStorage.getItem(STORAGE_TOKEN_KEY);
    if (!token) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    performRefresh().catch(() => {
      // Silent: the response interceptor will handle a real expiry on the
      // next user-initiated request.
    });
  }, REFRESH_INTERVAL_MS);
}

// ---------- Form draft persistence ----------

export function saveDraft(key, value) {
  try {
    localStorage.setItem(DRAFT_PREFIX + key, JSON.stringify(value));
  } catch (_e) {
    // localStorage full or unavailable; safe to ignore — draft is best-effort.
  }
}

export function loadDraft(key) {
  try {
    const raw = localStorage.getItem(DRAFT_PREFIX + key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_e) {
    return null;
  }
}

export function clearDraft(key) {
  try {
    localStorage.removeItem(DRAFT_PREFIX + key);
  } catch (_e) {}
}
